import React, { useState } from "react";
import Sidebar from "../../../components/Sidebar/Sidebar";
import AdminList from "../../../components/AdminList/AdminList";
import Modal from "../../../components/Modal/Modal";
import { getDharmaArticles, deleteDharmaArticle, createDharmaArticle, updateDharmaArticle } from "../../../utils/api";

const columns = [
  { key: "title", label: "Title" },
  { key: "author", label: "Author" },
  { key: "filetype", label: "File Type" },
  { 
    key: "createdOn", 
    label: "Created On",
    render: (value) => value ? new Date(value).toLocaleDateString() : "-"
  },
];

const filters = [
  {
    key: "filetype",
    label: "Filter by File Type",
    type: "select",
    options: [
      { value: "PDF", label: "PDF" },
      { value: "Video", label: "Video" },
      { value: "Article", label: "Article" },
    ]
  },
];

const initialFormData = {
  title: "",
  description: "",
  author: "Maha Sabha",
  filetype: "Article",
  content: "",
  fileUrl: "",
  imageUrl: ""
};

export default function DharmaLearning() {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);

  const handleAdd = () => {
    setFormData(initialFormData);
    setIsEditMode(false);
    setSelectedItem(null);
    setModalOpen(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setFormData({
      title: item.title || "",
      description: item.description || "",
      author: item.author || "Maha Sabha",
      filetype: item.filetype || "Article",
      content: item.content || "",
      fileUrl: item.fileUrl || "",
      imageUrl: item.imageUrl || ""
    });
    setIsEditMode(true);
    setModalOpen(true);
  };

  const handleView = (item) => {
    handleEdit(item);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          const maxSize = 500;
          let width = img.width;
          let height = img.height;
          
          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }
          
          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);
          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.5);
          
          setFormData(prev => ({ ...prev, imageUrl: compressedBase64 }));
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isEditMode && selectedItem) {
        await updateDharmaArticle(selectedItem._id, formData);
        alert("Updated successfully!");
      } else {
        await createDharmaArticle(formData);
        alert("Created successfully!");
      }
      setModalOpen(false);
      window.location.reload();
    } catch (error) {
      alert(error.message || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="dashboard-container">
      <Sidebar />
      <main className="dashboard-content">
        <AdminList
          title="Dharma Learning"
          fetchData={getDharmaArticles}
          deleteData={deleteDharmaArticle}
          columns={columns}
          onEdit={handleEdit}
          onAdd={handleAdd}
          onView={handleView}
          filters={filters}
          searchPlaceholder="Search dharma articles..."
        />
      </main>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        title={isEditMode ? "Edit Dharma Learning" : "Add New Dharma Learning"}
        size="large"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                required
                placeholder="Enter title"
              />
            </div>
            <div className="form-group">
              <label>Author</label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleFormChange}
                placeholder="Enter author name"
              />
            </div>
            <div className="form-group">
              <label>File Type</label>
              <select
                name="filetype"
                value={formData.filetype}
                onChange={handleFormChange}
              >
                <option value="Article">Article</option>
                <option value="PDF">PDF</option>
                <option value="Video">Video</option>
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                rows="3"
                placeholder="Enter description"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Content</h3>
            <div className="form-group">
              <label>Content</label>
              <textarea
                name="content"
                value={formData.content}
                onChange={handleFormChange}
                rows="8"
                placeholder="Enter content"
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Media</h3>
            <div className="form-group">
              <label>File URL</label>
              <input
                type="url"
                name="fileUrl"
                value={formData.fileUrl}
                onChange={handleFormChange}
                placeholder="https://..."
              />
            </div>
            <div className="form-group">
              <label>Cover Image</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="file-input"
              />
              {formData.imageUrl && (
                <div className="image-preview">
                  <img src={formData.imageUrl} alt="Preview" style={{ maxWidth: '100px', maxHeight: '100px' }} />
                  <button type="button" onClick={() => setFormData(prev => ({ ...prev, imageUrl: "" }))}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => setModalOpen(false)}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? "Saving..." : (isEditMode ? "Update" : "Create")}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
